
import { Component, Mixins, Prop } from "vue-property-decorator";
import { mapState } from "vuex";
import ShiftDetailModal from "./modals/shift-detail-modal.vue";
import RateShiftModal from "./modals/rate-shift-modal.vue";
import OpenShiftRequestModel from "./modals/shift-request-modal.vue";
import FilterPopover from "@/common/components/table/filter-popover.vue";
import { format as formatPhone } from '@/utils/phone.util';
import { formatDate } from '@/utils/date.util';
import TableMixin from '@/common/mixins/table.mixin';
import ColumnHeader from '@/common/components/table/column-header.vue';
import ActionButton from '@/common/components/table/action-button.vue';
import AuthenticateMixin from "../../common/mixins/auth.mixin";
import EmployeeDetails from "./modals/employee-details.vue";
import ValidatedFormMixin from "@/common/mixins/validated-form.mixin";
import ShiftMixin from "./shift.mixin";
import { RatingStatus, ShiftStatus } from "@/common/constants";
import { AcceptedShift, OpenShift, RequestedShift, Shift, EmployeeInfo } from "./shift.types";
import { OfficeDto } from "@/types/office.type";
import { RatingDto, RatingFormDto } from "@/types/rating.type";
import { ShiftDataDto } from "@/types/shift.type";

class TableFilters {
  location: string[] = [];
  shiftStatus: ShiftStatus[] = [];
  positionAbbreviation: string[] = [];
  name: string[] = [];
  email: string[] = [];
}

@Component({
  components: {
    ShiftDetailModal,
    OpenShiftRequestModel,
    FilterPopover,
    ColumnHeader,
    ActionButton,
    EmployeeDetails,
    RateShiftModal
  },
  computed: {
    ...mapState("Shift", 
    [
      "openShifts", 
      "requestedShifts",
      "confirmedShifts",
      "removedShifts",
      "expiredShifts",
      "selectedOffice",
      "offices",
      "shiftData"
    ])
  }
})
export default class ShiftTable extends Mixins(TableMixin, ShiftMixin, AuthenticateMixin, ValidatedFormMixin) {
  @Prop() currentUserFilter!: boolean;
  
  openShifts!: OpenShift[];
  requestedShifts!: RequestedShift[];
  confirmedShifts!: AcceptedShift[];
  removedShifts!: Shift[];
  expiredShifts!: Shift[];
  selectedOffice!: OfficeDto;
  offices!: OfficeDto[];
  shiftData!: ShiftDataDto;

  showDeleteModal = false;
  selectedShift: Shift | null = null;
  selectedRating: RatingDto | null = null;
  filters = new TableFilters();
  isEmployeeDetailsVisible = false;
  employeeInfo: EmployeeInfo | null = null;
  tableColumns = [
    { key: 'information', label: '', thStyle: { width: '50px' } },
    { key: 'location', label: 'Office', sortable: true, filterId: 'office-filter' },
    { key: 'shiftStatus', sortable: true, filterId: 'shift-status-filter' },
    { key: 'date', sortable: true },
    { key: 'actions', thStyle: { width: '160px' }, sortable: false },
    { key: 'shiftRating', label: 'Rate Shift' },
    { 
      key: 'positionAbbreviation',
      label: 'Position',
      sortable: true,
      filterId: 'position-abbreviation-filter',
      filterText: 'position'
    },
    { key: 'name', label: 'Dental Temp', sortable: true, filterId: 'name-filter'},
    { key: 'email', sortable: true, filterId: 'email-filter' },
    { key: 'phone', label: 'Phone Number' }
  ];

  formatPhone = formatPhone;
  formatDate = formatDate;

  ShiftStatus = ShiftStatus;
  RatingStatus = RatingStatus;

  get tableData(): Shift[] {
    const shifts = [
      ...this.openShifts,
      ...this.requestedShifts,
      ...this.confirmedShifts,
      ...this.removedShifts,
      ...this.expiredShifts
    ];

    if (!this.currentUserFilter) {
      return shifts;
    }

    return shifts.filter(shift => shift.postedByUserId === this.userInfo()?.sub);
  }

  async created(): Promise<void> {
    this.setLoading(true);
    
    this.timerRef = setInterval(() => {
      this.setLoading(true);

      this.getShifts(false);

      this.setLoading(false);
    }, 60000);

    this.setLoading(false);
  }

  destroyed(): void {
    clearInterval(this.timerRef);
  }

  async onDateApply(): Promise<void> {
    this.setLoading(true);

    await this.getShifts(false);

    this.setLoading(false);
  }

  showEmployeeDetailsModal(shift: AcceptedShift): void {
    this.employeeInfo = {
      id: shift.userId,
      name: shift.name,
      timeIn: this.getTimes(shift.time, 0, " - "),
      timeOut: this.getTimes(shift.time, 1, " - "),
      position: shift.position,
      rate: shift.rate,
      initials: this.getInitials(shift.name),
      officeId: shift.officeId,
      email: shift.email,
      phone: shift.phone,
      rating: shift.rating,
      date: new Date(shift.date),
      isFavorite: shift.isFavorite
    };

    this.isEmployeeDetailsVisible = true;
  }

  showShiftRequestModal(shift: RequestedShift): void {
    this.selectedShift = shift;
    this.$bvModal.show('shift-request-modal');
  }

  showInfoModal(shift: Shift): void {
    this.selectedShift = shift;
    this.showDeleteModal = false;
    this.$store.commit("Shift/updateField", {
      path: "selectedOffice",
      value: this.offices.find(office => office.id === shift.officeId)
    });
    this.$bvModal.show('shift-detail-modal');
  }

  showRateShiftModel(rating: RatingDto): void {
    this.selectedRating = rating;
    this.$bvModal.show('rate-shift-modal');
  }

  async deleteShift(shift: Shift): Promise<void> {
    this.selectedShift = shift;
    this.showDeleteModal = true;
    this.$bvModal.show('shift-detail-modal');
  }

  editShift(id: number): void {
    this.$emit("edit", id);
  }

  getShiftStatusIcon(shiftStatus: string): string {
    switch (shiftStatus) {
      case ShiftStatus.Open.value:
        return "posted fa-calendar-check";
      case ShiftStatus.Expired.value:
      case ShiftStatus.ExpiredV2.value:
      case ShiftStatus.Removed.value:
        return "fa-calendar-times";
      default:
        return "fa-calendar-check";
    }
  }

  getShiftStatusText(shiftStatus: string): string {
    return shiftStatus.replace(/([A-Z])/g, ' $1').trim();
  }

  resetFilters(): void {
    this.filters = new TableFilters();
  }

  async saveRating(form: RatingFormDto): Promise<void> {
    this.setLoading(true);

    await this.$store.dispatch("Ratings/updateRating", {
      id: form.id,
      rating: form.rating,
      comments: form.comments,
      status: RatingStatus.Active.getId(this.shiftData.ratingStatuses),
      raterUserId: this.userInfo()?.sub,
      noShow: form.noShow,
      sickCall: form.sickCall
    } as RatingFormDto);
    
    await this.getShifts(false);

    this.$bvModal.hide('rate-shift-modal');

    this.setLoading(false);
  }

  async dismissRating(form: RatingFormDto): Promise<void> {
    this.setLoading(true);

    await this.$store.dispatch("Ratings/updateRating", {
      id: form.id,
      rating: 0,
      comments: null,
      status: RatingStatus.Dismissed.getId(this.shiftData.ratingStatuses),
      raterUserId: this.userInfo()?.sub
    } as RatingFormDto);

    await this.getShifts(false);

    this.$bvModal.hide('rate-shift-modal');

    this.setLoading(false);
  }

  async onDelete(): Promise<void> {
    this.setLoading(true);

    await this.getShifts(false);

    this.setLoading(false);
  }
}
